// WHAT: Evening reflection step component with three structured questions
// WHY:  Provides guided reflection interface for users to reflect on their day with specific prompts
// DOMAIN: Productivity → Miracle Flow → Session Steps
// DEPENDENCIES: Textarea component, Stack layout, React controlled inputs
// RISKS:
//   - NOTE: Questions are hardcoded in German - may need internationalization
//   - NOTE: Textarea values are controlled by parent - may lose data on unmount
//   - NOTE: No validation for empty responses - users may skip questions

import { Brain } from "lucide-react";
import React from "react";

import { Stack, Heading, Textarea, Card } from "@/components/ui";
import { cn } from "@/shared/utils";

export interface EveningReflectionStepProperties {
  className?: string;
  onQuestion1Change: (value: string) => void;
  onQuestion2Change: (value: string) => void;
  onQuestion3Change: (value: string) => void;
  question1: string; // "Was hast du heute Gutes getan?"
  question2: string; // "Was hast du heute gelernt?"
  question3: string; // "Was waren die tollen Dinge heute?"
}

/**
 * WHAT:
 *   Evening reflection step component with three structured reflection questions.
 *
 * WHY:
 *   Provides guided reflection interface for users to reflect on their day with specific prompts,
 *   promoting mindfulness and gratitude practice as part of the evening routine.
 *
 * PROPS:
 *   - question1: string — Response to "Was hast du heute Gutes getan?" (What good did you do today?)
 *   - question2: string — Response to "Was hast du heute gelernt?" (What did you learn today?)
 *   - question3: string — Response to "Was waren die tollen Dinge heute?" (What were the great things today?)
 *   - onQuestion1Change: (value: string) => void — Callback for question 1 text changes
 *   - onQuestion2Change: (value: string) => void — Callback for question 2 text changes
 *   - onQuestion3Change: (value: string) => void — Callback for question 3 text changes
 *   - className?: string — Optional CSS classes for styling
 *
 * BEHAVIOR:
 *   - Displays three separate textarea inputs for each reflection question
 *   - Questions are hardcoded in German (may need internationalization)
 *   - All textarea values are controlled by parent component
 *   - Uses evening-specific color scheme (orange gradient)
 *   - No validation - users can submit empty responses
 *
 * RISKS:
 *   - NOTE: Questions are hardcoded in German - may need internationalization for global users
 *   - NOTE: Textarea values are controlled by parent - may lose data on unmount if not saved
 *   - NOTE: No validation for empty responses - users may skip questions without feedback
 *   - WARN: No character limits - very long responses may cause performance issues
 *
 * @param root0
 *
 * @param root0.question1
 *
 * @param root0.question2
 *
 * @param root0.question3
 *
 * @param root0.onQuestion1Change
 *
 * @param root0.onQuestion2Change
 *
 * @param root0.onQuestion3Change
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const EveningReflectionStep: React.FC<EveningReflectionStepProperties> = ({
  className,
  onQuestion1Change,
  onQuestion2Change,
  onQuestion3Change,
  question1,
  question2,
  question3,
}) => {
  return (
    <div className={cn("space-y-6 w-full", className)}>
      {/* Hero Section */}
      <Stack align="center" className="py-4 w-full" direction="col" gap={4}>
        <div className="flex items-center justify-center w-20 h-20 rounded-tracq bg-gradient-to-br from-tracq-deep-orange to-tracq-light-orange shadow-tracq-card">
          <Brain className="w-10 h-10 text-white" />
        </div>
        <Stack align="center" className="w-full max-w-4xl" direction="col" gap={2}>
          <Heading className="font-bold text-center text-tracq-light-orange" level="h3" size="xl">
            Evening Reflection
          </Heading>
          <p className="text-[#CBD5E1] text-center text-lg w-full px-4">
            Take a moment to reflect on your day. What went well? What did you learn? What are you
            grateful for?
          </p>
        </Stack>
      </Stack>

      {/* Reflection Questions */}
      <div className="space-y-4 w-full">
        {/* Question 1 */}
        <Card className="bg-[#1E293B] border-[#334155]" padding="md" variant="default">
          <Stack direction="col" gap={3}>
            <label className="block text-sm font-semibold text-[#CBD5E1] flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-tracq-light-orange/20 text-tracq-light-orange text-xs font-bold">
                1
              </span>
              Was hast du heute Gutes getan?
            </label>
            <Textarea
              className="bg-[#334155] border-[#475569] text-[#F1F5F9] placeholder:text-[#94A3B8] min-h-[120px] text-base leading-relaxed"
              onChange={(value: string) => {
                onQuestion1Change(value);
              }}
              placeholder="Think about the positive actions you took today. What did you do that made a difference? How did you help others or yourself?"
              rows={5}
              value={question1}
            />
          </Stack>
        </Card>

        {/* Question 2 */}
        <Card className="bg-[#1E293B] border-[#334155]" padding="md" variant="default">
          <Stack direction="col" gap={3}>
            <label className="block text-sm font-semibold text-[#CBD5E1] flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-tracq-light-orange/20 text-tracq-light-orange text-xs font-bold">
                2
              </span>
              Was hast du heute gelernt?
            </label>
            <Textarea
              className="bg-[#334155] border-[#475569] text-[#F1F5F9] placeholder:text-[#94A3B8] min-h-[120px] text-base leading-relaxed"
              onChange={(value: string) => {
                onQuestion2Change(value);
              }}
              placeholder="Reflect on the lessons and insights from today. What new knowledge did you gain? What experiences taught you something valuable?"
              rows={5}
              value={question2}
            />
          </Stack>
        </Card>

        {/* Question 3 */}
        <Card className="bg-[#1E293B] border-[#334155]" padding="md" variant="default">
          <Stack direction="col" gap={3}>
            <label className="block text-sm font-semibold text-[#CBD5E1] flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-tracq-light-orange/20 text-tracq-light-orange text-xs font-bold">
                3
              </span>
              Was waren die tollen Dinge heute?
            </label>
            <Textarea
              className="bg-[#334155] border-[#475569] text-[#F1F5F9] placeholder:text-[#94A3B8] min-h-[120px] text-base leading-relaxed"
              onChange={(value: string) => {
                onQuestion3Change(value);
              }}
              placeholder="Celebrate the good moments of your day. What made you smile? What are you grateful for? What moments brought you joy?"
              rows={5}
              value={question3}
            />
          </Stack>
        </Card>
      </div>
    </div>
  );
};
